import React, { useState, useEffect } from "react";
import { BOOKING_URL } from "@/lib/site";
import { BookLink } from "@/components/site/BookLink";
import { PageHero, Eyebrow } from "@/components/site/Section";
import { useContent } from "@/context/ContentContext";
import { X } from "lucide-react";

const RESULTS = [
  { title: "Botox Expression Lines", img: "/assets/gallery/botox-expression.jpg" },
  { title: "RF Skin Tightening", img: "/assets/gallery/rf-abdomen.jpg" },
  { title: "Jawline Rejuvenation", img: "/assets/gallery/jawline.jpg" },
  { title: "Botox Forehead Results", img: "/assets/gallery/botox-forehead.jpg" },
  { title: "Neck Rejuvenation", img: "/assets/gallery/neck-collage.jpg" },
  { title: "Body Sculpting — Second Session", img: "/assets/gallery/body-sculpt-second-session.jpg" },
  { title: "Lip Enhancement", img: "/assets/gallery/lip-filler.jpg" },
  { title: "Seraphina Neck Lift", img: "/assets/gallery/neck-lift.jpg" },
  { title: "Body Sculpting — One Treatment", img: "/assets/gallery/body-sculpt-one-treatment.jpg" },
  { title: "Abdominal Sculpting", img: "/assets/gallery/abdomen-collage.jpg" },
];

export default function BeforeAfter() {
  const { gallery = RESULTS } = useContent();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = lightbox ? "hidden" : "";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div data-testid="before-after-page">
      <PageHero
        eyebrow="Before & After"
        title="Real Clients. Beautiful Results."
        subtitle="Explore examples of personalized treatments and natural-looking outcomes."
      />

      <section className="py-16 bg-[#fffaf6]">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-10">
          <div className="text-center max-w-[780px] mx-auto mb-10">
            <Eyebrow className="mb-2">Real Client Results</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)]">Before &amp; After Gallery</h2>
            <p className="text-[var(--charcoal-soft)] mt-3">
              Explore real treatment outcomes from Seraphina Collective. Individual results vary by treatment plan and client.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-7">
            {gallery.map((r) => (
              <figure key={r.title} className="soft-card rounded-2xl overflow-hidden m-0">
                <button
                  type="button"
                  onClick={() => setLightbox(r)}
                  className="block w-full bg-[#f4ebe4] cursor-zoom-in group"
                  aria-label={`View ${r.title} larger`}
                >
                  <img
                    src={r.img}
                    alt={`${r.title} before and after result`}
                    loading="lazy"
                    className="w-full max-h-[720px] object-contain transition-transform duration-300 group-hover:scale-[1.015]"
                  />
                </button>
                <figcaption className="px-6 py-5">
                  <h3 className="font-display text-2xl text-[var(--charcoal)]">{r.title}</h3>
                  <p className="text-xs tracking-wide text-[var(--charcoal-soft)] mt-1">Individual results may vary.</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <p className="text-center text-xs text-[var(--charcoal-soft)] mt-10 max-w-2xl mx-auto leading-relaxed">
            Photos are provided for educational and promotional purposes with client approval. Results are not guaranteed and may vary.
          </p>
          <div className="text-center mt-8">
            <BookLink label="Before After Book" href={BOOKING_URL} className="btn-gold rounded-md px-8 py-3.5 inline-block">
              Book a Consultation
            </BookLink>
          </div>
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center p-8"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="fixed right-6 top-4 w-12 h-12 rounded-full border border-white/50 bg-black/45 text-white flex items-center justify-center"
            onClick={() => setLightbox(null)}
            aria-label="Close image"
          >
            <X size={26} />
          </button>
          <img src={lightbox.img} alt={lightbox.title} className="max-w-[94vw] max-h-[90vh] object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
